import type {
  OpsTrafficClass,
  OpsTrafficOverviewResponse,
  OpsTrafficQuery,
  OpsTrafficWindowPreset,
} from '@nadoutong/contracts';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/traffic';

export type OpsTrafficWindow = OpsTrafficWindowPreset;
export type OpsTrafficTrafficClass = OpsTrafficClass;

export interface OpsTrafficOverviewQuery extends OpsTrafficQuery {
  readonly window: OpsTrafficWindow;
  readonly trafficClass: OpsTrafficTrafficClass;
}

export interface UseOpsTrafficOptions {
  readonly autoRefresh: boolean;
}

function buildQuery(query: OpsTrafficOverviewQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query) as Array<[string, string | undefined]>) {
    if (value !== undefined && value.length > 0) {
      params.set(key, value);
    }
  }
  return `?${params.toString()}`;
}

export async function getOpsTrafficOverview(
  query: OpsTrafficOverviewQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsTrafficOverviewResponse> {
  return opsApiRequest<OpsTrafficOverviewResponse>(
    `${BASE}/overview${buildQuery(query)}`,
    { signal },
    fetchImpl,
  );
}

export function useOpsTrafficOverview(
  query: OpsTrafficOverviewQuery,
  options: UseOpsTrafficOptions,
): UseQueryResult<OpsTrafficOverviewResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['ops-traffic', 'overview', query],
    queryFn: ({ signal }) => getOpsTrafficOverview(query, fetchImpl, signal),
    // Opt-in only, and never for a hidden tab (#2278): one overview is a
    // TEMP TABLE materialisation over request_lifecycle_metrics ⋈ usage_logs
    // ⋈ usage_rated_events on the shared production RDS, and a perspective
    // page re-keys its money slices on every poll. A tab left open in the
    // background must cost the database nothing.
    refetchInterval: options.autoRefresh ? 60_000 : false,
    refetchIntervalInBackground: false,
  });
}
